import { useState, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import {
    Plus,
    Upload,
    Search,
    Filter,
    ArrowUpRight,
    ArrowDownLeft,
    CheckCircle2,
    CircleDashed,
    ArchiveX,
    Package,
    AlertCircle,
    X,
    RotateCw,
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
    CalendarClock,
    CircleOff,
    CalendarCheck,
    TriangleAlert
} from 'lucide-react';
import { useAuthStore } from '../hooks/useAuth';
import { useUIStore } from '../hooks/useUIStore';
import { useAutoRefreshInterval } from '../hooks/useAutoRefreshInterval';
import { PODetailsDrawer, formatPoCalendarDate } from './PODetailsDrawer';
import { POImportWizard } from './po/POImportWizard';
import { POImportHistory } from './po/POImportHistory';
import { useLocation } from 'wouter';
import './POManager.css';

interface PurchaseOrder {
    id: string;
    customerPoNumber: string;
    clientName: string;
    type: 'inbound' | 'outbound';
    status: 'open' | 'closed' | 'on_hold';
    totalSkids?: number;
    totalWeight?: number;
    carrierEmail?: string;
    description?: string;
    createdAt?: string;
    dueDate?: string | null;
    priority?: 'standard' | 'urgent' | 'critical';

    holdReason?: string;
    holdNotes?: string;
    holdSetAt?: string;
    holdSetBy?: string;
    holdReleasedAt?: string;
    holdReleasedBy?: string;
    holdReleaseReason?: string;

    // Phase 3: Shortage Resolution
    parentPoId?: string | null;
    resolutionStatus?: string | null;
    followupNumber?: number | null;

    // Appointment / Dock fields
    appointmentId?: string | null;
    scheduledAt?: string | null;
    appointmentStatus?: string | null;
    dockId?: string | null;
    dockNumber?: string | null;
}

// Persistence Key
const PREFS_KEY = 'bw_po_prefs_v1';

type SortMode = 'newest' | 'oldest' | 'po_asc' | 'po_desc' | 'client_asc' | 'client_desc' | 'skids_asc' | 'skids_desc';
type TypeFilter = 'all' | 'inbound' | 'outbound';
type StatusFilter = 'all' | 'open' | 'on_hold' | 'closed';
type AttentionFilter = 'unscheduled' | 'missing_dock' | 'today' | 'shortage' | null;

// Column sorting helpers
type SortableColumn = 'po' | 'client' | 'created' | 'skids' | 'scheduled';

function getSortModeForColumn(column: SortableColumn, currentSort: SortMode): SortMode {
    switch (column) {
        case 'po':
            return currentSort === 'po_asc' ? 'po_desc' : 'po_asc';
        case 'client':
            return currentSort === 'client_asc' ? 'client_desc' : 'client_asc';
        case 'created':
            return currentSort === 'newest' ? 'oldest' : 'newest';
        case 'skids':
            return currentSort === 'skids_desc' ? 'skids_asc' : 'skids_desc';
        case 'scheduled':
            // no dedicated sort mode — toggle newest/oldest as proxy
            return currentSort === 'newest' ? 'oldest' : 'newest';
        default:
            return 'newest';
    }
}

function getColumnSortState(column: SortableColumn, currentSort: SortMode): 'asc' | 'desc' | null {
    switch (column) {
        case 'po':
            if (currentSort === 'po_asc') return 'asc';
            if (currentSort === 'po_desc') return 'desc';
            return null;
        case 'client':
            if (currentSort === 'client_asc') return 'asc';
            if (currentSort === 'client_desc') return 'desc';
            return null;
        case 'created':
            if (currentSort === 'newest') return 'desc';
            if (currentSort === 'oldest') return 'asc';
            return null;
        case 'skids':
            if (currentSort === 'skids_asc') return 'asc';
            if (currentSort === 'skids_desc') return 'desc';
            return null;
        default:
            return null;
    }
}

// Formatting helpers
function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatScheduled(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Hardened fetcher
const fetcher = async (url: string, token: string) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
        throw new Error(`Failed to fetch POs: ${res.status} ${res.statusText}`);
    }
    return res.json();
}

export function POManager() {
    const { token, user } = useAuthStore();
    const { togglePOWizard } = useUIStore();
    const [, setLocation] = useLocation();
    const [showImportWizard, setShowImportWizard] = useState(false);
    const [activeTab, setActiveTab] = useState<'pos' | 'history'>('pos');
    const hasWarehouse = Boolean(user?.warehouseId);

    // Auto-refresh subscription
    const refreshInterval = useAutoRefreshInterval();

    // Data Fetching with SWR (only fetch once user has a warehouse assigned — D26)
    const { data: rawPOs, error, isLoading, mutate } = useSWR(
        token && hasWarehouse ? ['/api/warehouse-pos', token] : null,
        ([url, token]) => fetcher(url, token),
        {
            refreshInterval
        }
    );

    // Hold Modal State
    const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
    const [holdPO, setHoldPO] = useState<PurchaseOrder | null>(null);
    const [holdReason, setHoldReason] = useState('damage');
    const [holdNotes, setHoldNotes] = useState('');
    const [isHolding, setIsHolding] = useState(false);

    const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
    const [releasePO, setReleasePO] = useState<PurchaseOrder | null>(null);
    const [releaseReason, setReleaseReason] = useState('');
    const [isReleasing, setIsReleasing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
    const [sortMode, setSortMode] = useState<SortMode>('newest');
    const [attentionFilter, setAttentionFilter] = useState<AttentionFilter>(null);

    // Drawer State
    const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // Load preferences on mount
    useEffect(() => {
        const saved = localStorage.getItem(PREFS_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed.typeFilter) setTypeFilter(parsed.typeFilter);
                if (parsed.sortMode) setSortMode(parsed.sortMode);
            } catch (e) {
                console.warn('Failed to parse PO prefs', e);
            }
        }
    }, []);

    // Save preferences on change
    useEffect(() => {
        localStorage.setItem(PREFS_KEY, JSON.stringify({
            typeFilter,
            sortMode
        }));
    }, [typeFilter, sortMode]);

    // Status counts (computed from raw POs before filtering)
    const statusCounts = useMemo(() => {
        if (!rawPOs) return { all: 0, open: 0, on_hold: 0, closed: 0 };
        const counts = { all: 0, open: 0, on_hold: 0, closed: 0 };
        rawPOs.forEach((po: PurchaseOrder) => {
            counts.all++;
            if (po.status === 'open') counts.open++;
            else if (po.status === 'on_hold') counts.on_hold++;
            else if (po.status === 'closed') counts.closed++;
        });
        return counts;
    }, [rawPOs]);

    // Attention counts (computed from raw POs, independent of filters)
    const attentionCounts = useMemo(() => {
        if (!rawPOs) return { unscheduled: 0, missing_dock: 0, today: 0, shortage: 0 };
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const todayEnd = todayStart + 86400000; // +24h
        const counts = { unscheduled: 0, missing_dock: 0, today: 0, shortage: 0 };
        rawPOs.forEach((po: PurchaseOrder) => {
            if (po.status === 'open' && !po.appointmentId) counts.unscheduled++;
            if (po.appointmentId && po.scheduledAt && !po.dockNumber) counts.missing_dock++;
            if (po.scheduledAt) {
                const t = new Date(po.scheduledAt).getTime();
                if (t >= todayStart && t < todayEnd) counts.today++;
            }
            if (po.status !== 'closed' && !po.parentPoId && po.resolutionStatus === 'unresolved') counts.shortage++;
        });
        return counts;
    }, [rawPOs]);

    // Attention filter predicate
    const matchesAttention = (po: PurchaseOrder): boolean => {
        if (!attentionFilter) return true;
        switch (attentionFilter) {
            case 'unscheduled': return po.status === 'open' && !po.appointmentId;
            case 'missing_dock': return !!(po.appointmentId && po.scheduledAt && !po.dockNumber);
            case 'today': {
                if (!po.scheduledAt) return false;
                const now = new Date();
                const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
                const t = new Date(po.scheduledAt).getTime();
                return t >= todayStart && t < todayStart + 86400000;
            }
            case 'shortage': return po.status !== 'closed' && !po.parentPoId && po.resolutionStatus === 'unresolved';
            default: return true;
        }
    };

    // Derived State: Filter & Sort
    const filteredAndSortedPOs = useMemo(() => {
        if (!rawPOs) return [];

        let result = [...rawPOs];

        // 1. Attention filter (overrides status filter)
        if (attentionFilter) {
            result = result.filter(matchesAttention);
        }

        // 2. Filter by Status (skipped when attention filter is active — attention already narrows)
        if (!attentionFilter && statusFilter !== 'all') {
            result = result.filter((po: PurchaseOrder) => po.status === statusFilter);
        }

        // 3. Filter by Type
        if (typeFilter !== 'all') {
            result = result.filter((po: PurchaseOrder) => po.type === typeFilter);
        }

        // 4. Search
        if (searchTerm.trim()) {
            const lowerQuery = searchTerm.toLowerCase();
            result = result.filter((po: PurchaseOrder) =>
                (po.customerPoNumber || '').toLowerCase().includes(lowerQuery) ||
                (po.clientName || '').toLowerCase().includes(lowerQuery) ||
                (po.type && po.type.toLowerCase().includes(lowerQuery))
            );
        }

        // 5. Sort
        result.sort((a: PurchaseOrder, b: PurchaseOrder) => {
            switch (sortMode) {
                case 'newest':
                    return (b.createdAt || b.id).localeCompare(a.createdAt || a.id);
                case 'oldest':
                    return (a.createdAt || a.id).localeCompare(b.createdAt || b.id);
                case 'po_asc':
                    return (a.customerPoNumber || '').localeCompare(b.customerPoNumber || '');
                case 'po_desc':
                    return (b.customerPoNumber || '').localeCompare(a.customerPoNumber || '');
                case 'client_asc':
                    return (a.clientName || '').localeCompare(b.clientName || '');
                case 'client_desc':
                    return (b.clientName || '').localeCompare(a.clientName || '');
                case 'skids_asc':
                    return (a.totalSkids || 0) - (b.totalSkids || 0);
                case 'skids_desc':
                    return (b.totalSkids || 0) - (a.totalSkids || 0);
                default:
                    return 0;
            }
        });

        return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawPOs, searchTerm, typeFilter, statusFilter, sortMode, attentionFilter]);

    // Helpers
    const handleReset = () => {
        setSearchTerm('');
        setTypeFilter('all');
        setStatusFilter('open');
        setSortMode('newest');
        setAttentionFilter(null);
        localStorage.removeItem(PREFS_KEY);
    };

    const handleAttentionClick = (key: AttentionFilter) => {
        if (attentionFilter === key) {
            setAttentionFilter(null); // deselect
        } else {
            setAttentionFilter(key);
            setStatusFilter('all'); // reset status pill to avoid hidden conflicts
        }
    };

    const handleStatusPillClick = (key: StatusFilter) => {
        setStatusFilter(key);
        setAttentionFilter(null); // clear attention filter when using status pills
    };

    const handleHoldSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!holdPO) return;
        setIsHolding(true);
        try {
            const res = await fetch(`/api/warehouse-pos/${holdPO.id}/hold`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${useAuthStore.getState().token}` },
                body: JSON.stringify({ reason: holdReason, notes: holdNotes }),
            });
            if (!res.ok) throw new Error(await res.text());
            await mutate();
            setIsHoldModalOpen(false);
            setHoldPO(null);
            setHoldNotes('');
        } catch (err) {
            console.error('Failed to hold PO', err);
            alert('Failed to hold PO');
        } finally {
            setIsHolding(false);
        }
    };

    const handleReleaseSubmit = async () => {
        if (!releasePO || !releaseReason.trim()) return;
        setIsReleasing(true);
        try {
            const res = await fetch(`/api/warehouse-pos/${releasePO.id}/release-hold`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${useAuthStore.getState().token}` },
                body: JSON.stringify({ reason: releaseReason.trim() }),
            });
            if (!res.ok) throw new Error(await res.text());
            await mutate();
            setIsReleaseModalOpen(false);
            setReleasePO(null);
            setReleaseReason('');
        } catch (err) {
            console.error('Failed to release PO', err);
            alert('Failed to release PO');
        } finally {
            setIsReleasing(false);
        }
    };

    const handleRowClick = (po: PurchaseOrder) => {
        setSelectedPO(po);
        setIsDrawerOpen(true);
    };

    const handleColumnSort = (column: SortableColumn) => {
        setSortMode(getSortModeForColumn(column, sortMode));
    };

    // Sort indicator component
    const SortIndicator = ({ column }: { column: SortableColumn }) => {
        const state = getColumnSortState(column, sortMode);
        if (state === 'asc') return <ChevronUp size={14} className="text-slate-700" />;
        if (state === 'desc') return <ChevronDown size={14} className="text-slate-700" />;
        return <ChevronsUpDown size={14} className="text-slate-400" />;
    };

    // Render inline status indicators (HOLD / SHORT / F-U / R#)
    const renderStatusIndicators = (po: PurchaseOrder) => {
        const indicators: React.ReactNode[] = [];
        const isHeld = po.status === 'on_hold';

        if (isHeld) {
            indicators.push(
                <span key="hold" className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded ml-1.5">
                    <AlertCircle size={10} />
                    HOLD
                </span>
            );
        }
        if (po.parentPoId) {
            indicators.push(
                <span key="followup" className="inline-flex items-center text-[9px] font-medium tracking-wide text-slate-500 bg-slate-50 border border-slate-200/80 px-1.5 py-0.5 rounded ml-1.5">
                    Revision {po.followupNumber || '?'}
                </span>
            );
        }
        if (!po.parentPoId && po.resolutionStatus === 'unresolved') {
            indicators.push(
                <span key="short" className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded ml-1.5">
                    ⚠️ Shortage
                </span>
            );
        }
        if (!po.parentPoId && po.resolutionStatus === 'followup_created') {
            indicators.push(
                <span key="fu" className="inline-flex items-center text-[9px] font-normal tracking-wide text-slate-300 bg-transparent border border-slate-200/40 px-1.5 py-0.5 rounded ml-1.5 group-hover:text-slate-500 group-hover:bg-slate-50 group-hover:border-slate-200 transition-colors">
                    Follow-up
                </span>
            );
        }

        return indicators;
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'open':
                return (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">
                        <CircleDashed size={12} />
                        Open
                    </span>
                );
            case 'on_hold':
                return (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                        <AlertCircle size={12} />
                        On Hold
                    </span>
                );
            case 'closed':
                return (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                        <CheckCircle2 size={12} />
                        Closed
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded">
                        {status}
                    </span>
                );
        }
    };

    const getTypeBadge = (type: string) => {
        if (type === 'inbound') {
            return (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                    <ArrowDownLeft size={12} />
                    Inbound
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                <ArrowUpRight size={12} />
                Outbound
            </span>
        );
    };

    // Status filter pill config
    const statusPills: { key: StatusFilter; label: string; count: number }[] = [
        { key: 'all', label: 'All', count: statusCounts.all },
        { key: 'open', label: 'Open', count: statusCounts.open },
        { key: 'on_hold', label: 'On Hold', count: statusCounts.on_hold },
        { key: 'closed', label: 'Closed', count: statusCounts.closed },
    ];

    const hasActiveFilters = searchTerm || typeFilter !== 'all' || statusFilter !== 'open' || sortMode !== 'newest' || attentionFilter !== null;

    // Natural-language label helper with pluralization (title case)
    const getAttentionLabel = (key: NonNullable<AttentionFilter>, count: number): string => {
        switch (key) {
            case 'unscheduled': return count === 1 ? 'Needs Scheduling' : 'Need Scheduling';
            case 'missing_dock': return 'Missing Dock';
            case 'today': return 'Arriving Today';
            case 'shortage': return count === 1 ? 'Shortage Review' : 'Shortage Reviews';
            default: return key;
        }
    };

    // Attention card config
    const attentionCards: { key: NonNullable<AttentionFilter>; count: number; icon: React.ReactNode; color: string; activeColor: string }[] = [
        { key: 'unscheduled', count: attentionCounts.unscheduled, icon: <CalendarClock size={15} />, color: 'text-amber-600', activeColor: 'ring-amber-400 bg-amber-50' },
        { key: 'missing_dock', count: attentionCounts.missing_dock, icon: <CircleOff size={15} />, color: 'text-orange-500', activeColor: 'ring-orange-400 bg-orange-50' },
        { key: 'today', count: attentionCounts.today, icon: <CalendarCheck size={15} />, color: 'text-blue-500', activeColor: 'ring-blue-400 bg-blue-50' },
        { key: 'shortage', count: attentionCounts.shortage, icon: <TriangleAlert size={15} />, color: 'text-purple-500', activeColor: 'ring-purple-400 bg-purple-50' },
    ];

    if (isLoading) return (
        <div className="h-full flex flex-col overflow-hidden py-6">
            <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 space-y-4">
                {/* Skeleton header */}
                <div className="flex justify-between items-center">
                    <div className="space-y-2">
                        <div className="h-8 w-52 bg-slate-200 rounded animate-pulse" />
                        <div className="h-4 w-72 bg-slate-200/60 rounded animate-pulse" />
                    </div>
                    <div className="h-9 w-24 bg-slate-200 rounded-lg animate-pulse" />
                </div>
                {/* Skeleton table */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                    <div className="bg-slate-50 border-b border-slate-200 px-3 py-2.5">
                        <div className="flex gap-6">
                            {[80, 140, 56, 72, 40, 40, 96, 72].map((w, i) => (
                                <div key={i} className="h-3 rounded animate-pulse bg-slate-200" style={{ width: w }} />
                            ))}
                        </div>
                    </div>
                    {Array.from({ length: 7 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-6 px-3 py-3 border-b border-slate-100">
                            <div className="h-4 w-20 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-36 bg-slate-100 rounded animate-pulse" />
                            <div className="h-5 w-16 bg-slate-100 rounded animate-pulse" />
                            <div className="h-5 w-16 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-8 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-10 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-24 bg-slate-100 rounded animate-pulse" />
                            <div className="h-4 w-16 bg-slate-100 rounded animate-pulse" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    if (!hasWarehouse) return (
        <div className="h-full flex flex-col items-center justify-center p-8 text-center space-y-3">
            <Package size={36} className="text-primary" />
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Set up your warehouse facility first</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md">
                Complete your facility onboarding so you can create and manage purchase orders for your docks.
            </p>
            <button
                onClick={() => setLocation('/facilities?from=onboarding')}
                className="mt-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-semibold transition-colors"
            >
                Configure Facility
            </button>
        </div>
    );

    if (error) return (
        <div className="h-full flex flex-col items-center justify-center text-red-500 space-y-2">
            <ArchiveX size={32} />
            <p className="font-medium text-slate-900 dark:text-white">Failed to load purchase orders</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{error.message}</p>
            <button
                onClick={() => mutate()}
                className="mt-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors text-slate-700 font-medium"
            >
                Retry
            </button>
        </div>
    );

    // Active attention label for filter chip
    const activeAttentionLabel = attentionFilter
        ? (() => {
            const card = attentionCards.find(c => c.key === attentionFilter);
            if (!card) return attentionFilter;
            return `${card.count} ${getAttentionLabel(card.key, card.count)}`;
          })()
        : null;

    // Row emphasis helper
    const getRowEmphasis = (po: PurchaseOrder): string => {
        if (po.status === 'closed') return 'opacity-50';
        // Today arriving — blue left border + tint
        if (po.scheduledAt) {
            const now = new Date();
            const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
            const t = new Date(po.scheduledAt).getTime();
            if (t >= todayStart && t < todayStart + 86400000) return 'border-l-4 border-l-blue-400 bg-blue-50/40';
        }
        // Missing dock — orange left border + tint
        if (po.appointmentId && po.scheduledAt && !po.dockNumber) return 'border-l-4 border-l-orange-400 bg-orange-50/40';
        // Unscheduled open — amber left border + tint
        if (po.status === 'open' && !po.appointmentId) return 'border-l-4 border-l-amber-400 bg-amber-50/40';
        return '';
    };

    return (
        <div className="h-full flex flex-col overflow-hidden py-6 mob-header-pt">
          <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 flex flex-col flex-1 overflow-hidden">
            {/* Page Header */}
            <div className="shrink-0 mb-5">
                {/* Title + Create PO */}
                <div className="flex flex-wrap justify-between items-start gap-3 mb-5 pl-12 md:pl-0">
                    <div className="space-y-1">
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-outfit tracking-tight flex items-center gap-2.5">
                            <Package className="text-blue-600 dark:text-blue-400" size={22} />
                            {activeTab === 'pos' ? 'Purchase Orders' : 'Import History'}
                        </h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            {activeTab === 'pos' ? 'Manage inbound orders and sync with dock appointments.' : 'Audit trail for bulk PO imports.'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {activeTab === 'pos' ? (
                            <>
                                <button
                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors border border-slate-200"
                                    onClick={() => setActiveTab('history')}
                                >
                                    <CalendarClock size={16} />
                                    Import History
                                </button>
                                <button
                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors border border-slate-200"
                                    onClick={() => setShowImportWizard(true)}
                                >
                                    <Upload size={16} />
                                    Import POs
                                </button>
                                <button
                                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                                    onClick={() => togglePOWizard(true)}
                                >
                                    <Plus size={16} />
                                    New PO
                                </button>
                            </>
                        ) : (
                            <button
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors border border-slate-200"
                                onClick={() => setActiveTab('pos')}
                            >
                                <ArrowDownLeft size={16} />
                                Back to POs
                            </button>
                        )}
                    </div>
                </div>

                {activeTab === 'pos' && (
                    <div className="bg-white/70 border border-slate-200 rounded-xl px-3 py-2.5 flex flex-wrap items-center gap-2">
                        {/* Toolbar Card */}
                        {/* Status pills */}
                        <div className="flex items-center gap-1 flex-wrap">
                        {statusPills.map(pill => (
                            <button
                                key={pill.key}
                                onClick={() => handleStatusPillClick(pill.key)}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[12px] font-medium rounded transition-colors ${
                                    statusFilter === pill.key
                                        ? 'bg-slate-700 text-white'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                                {pill.label}
                                <span className={`text-[10px] font-mono px-1 py-px rounded ${
                                    statusFilter === pill.key
                                        ? 'bg-slate-600 text-white'
                                        : 'bg-slate-100 text-slate-400'
                                }`}>
                                    {pill.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Divider */}
                    <div className="w-px h-5 bg-slate-200 hidden sm:block" />

                    {/* Search */}
                    <div className="relative flex-1 min-w-[180px] max-w-sm">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search PO #, client…"
                            className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-8 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-300 outline-none transition-all"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {/* Filter Type */}
                    <div className="flex items-center gap-1.5">
                        <Filter size={14} className="text-slate-400" />
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
                            className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-slate-700 focus:ring-1 focus:ring-blue-500 outline-none cursor-pointer hover:bg-slate-50"
                        >
                            <option value="all">All Types</option>
                            <option value="inbound">Inbound</option>
                            <option value="outbound">Outbound</option>
                        </select>
                    </div>

                    {/* Issues / Attention Filter */}
                    <div className="flex items-center gap-1.5">
                        <TriangleAlert size={14} className="text-slate-400" />
                        <select
                            value={attentionFilter || 'all'}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'all') {
                                    setAttentionFilter(null);
                                } else {
                                    setAttentionFilter(val as AttentionFilter);
                                    setStatusFilter('all');
                                }
                            }}
                            className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-slate-700 focus:ring-1 focus:ring-blue-500 outline-none cursor-pointer hover:bg-slate-50"
                        >
                            <option value="all">All Issues</option>
                            {attentionCards.map(card => (
                                <option key={card.key} value={card.key}>
                                    {getAttentionLabel(card.key, card.count)} ({card.count})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Refresh & Reset */}
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => mutate()}
                            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Refresh Data"
                        >
                            <RotateCw size={15} />
                        </button>
                        {hasActiveFilters && (
                            <button
                                onClick={handleReset}
                                className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-2 py-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                Reset
                            </button>
                        )}
                    </div>
                </div>
                )}
            </div>

            {activeTab === 'history' ? (
                <div className="flex-1 overflow-hidden">
                    <POImportHistory />
                </div>
            ) : (
                <>
            {/* Active attention filter chip */}
            {activeAttentionLabel && (
                <div className="shrink-0 mb-2">
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-3 py-1">
                        Filtered by: <span className="font-semibold">{activeAttentionLabel}</span>
                        <button
                            onClick={() => setAttentionFilter(null)}
                            className="ml-0.5 text-slate-400 hover:text-slate-700 transition-colors"
                        >
                            <X size={13} />
                        </button>
                    </span>
                </div>
            )}

            {/* Table Area */}
            <div className="flex-1 overflow-y-auto pb-4">
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                        <thead>
                            <tr className="bg-slate-50/80 border-b border-slate-200">
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleColumnSort('po')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        PO # <SortIndicator column="po" />
                                    </span>
                                </th>
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleColumnSort('client')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        Supplier <SortIndicator column="client" />
                                    </span>
                                </th>
                                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5">
                                    Type
                                </th>
                                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5">
                                    Status
                                </th>
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleColumnSort('skids')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        Skids <SortIndicator column="skids" />
                                    </span>
                                </th>
                                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5">
                                    Dock
                                </th>
                                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5">
                                    Scheduled
                                </th>
                                <th
                                    className="text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2.5 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                    onClick={() => handleColumnSort('created')}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        Created <SortIndicator column="created" />
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAndSortedPOs.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center py-12">
                                        <div className="flex flex-col items-center text-slate-400">
                                            <ArchiveX size={40} className="mb-3 text-slate-300" />
                                            <p className="text-sm font-medium text-slate-500">No purchase orders found</p>
                                            <p className="text-xs text-slate-400 mt-1">
                                                {hasActiveFilters
                                                    ? 'Try adjusting your filters or search term'
                                                    : 'Create a new PO to get started'}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredAndSortedPOs.map((po: PurchaseOrder) => (
                                    <tr
                                        key={po.id}
                                        onClick={() => handleRowClick(po)}
                                        className={`group hover:bg-slate-50/70 border-b border-slate-100 cursor-pointer transition-colors ${getRowEmphasis(po)}`}
                                    >
                                        {/* PO # */}
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            <span className="font-mono text-[13px] text-slate-900 font-medium">
                                                {po.customerPoNumber}
                                            </span>
                                        </td>
                                        {/* Supplier */}
                                        <td className="px-3 py-2.5">
                                            <span className="block max-w-[180px] truncate text-[13px] text-slate-700" title={po.clientName}>
                                                {po.clientName}
                                            </span>
                                        </td>
                                        {/* Type */}
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            {getTypeBadge(po.type)}
                                        </td>
                                        {/* Status */}
                                        <td className="px-3 py-2.5 whitespace-nowrap">
                                            <div className="flex items-center flex-wrap gap-1">
                                                {getStatusBadge(po.status)}
                                                {renderStatusIndicators(po)}
                                            </div>
                                        </td>
                                        {/* Skids */}
                                        <td className="px-3 py-2.5 whitespace-nowrap text-[13px] text-slate-700 tabular-nums">
                                            {po.totalSkids != null ? po.totalSkids : '—'}
                                        </td>
                                        {/* Dock */}
                                        <td className="px-3 py-2.5 whitespace-nowrap text-[13px] text-slate-500 font-mono">
                                            {po.dockNumber || '—'}
                                        </td>
                                        {/* Scheduled / Due Date */}
                                        <td className="px-3 py-2.5 whitespace-nowrap text-[13px] text-slate-500">
                                            {po.scheduledAt ? (
                                                <div>
                                                    <div>{formatScheduled(po.scheduledAt)}</div>
                                                    {po.dueDate && (
                                                        <div className="text-[11px] text-slate-400">Due: {formatPoCalendarDate(po.dueDate)}</div>
                                                    )}
                                                </div>
                                            ) : po.dueDate ? (
                                                <span className="text-slate-700 font-medium">Due: {formatPoCalendarDate(po.dueDate)}</span>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        {/* Created */}
                                        <td className="px-3 py-2.5 whitespace-nowrap text-[13px] text-slate-500">
                                            {formatDate(po.createdAt)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
              </div>
            </div>

            <PODetailsDrawer
                po={selectedPO ? (rawPOs?.find((p: PurchaseOrder) => p.id === selectedPO.id) || selectedPO) : null}
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onUpdate={(updatedPo) => {
                    if (updatedPo) {
                        setSelectedPO(prev => (prev ? { ...prev, ...updatedPo } : (updatedPo as PurchaseOrder)));
                    }
                    mutate();
                }} // Refresh list & active drawer PO on updates
                onRequestHold={(po: PurchaseOrder) => {
                    setHoldPO(po);
                    setIsHoldModalOpen(true);
                    setIsDrawerOpen(false); // Close details drawer
                }}
                onRequestReleaseHold={(po: PurchaseOrder) => {
                    setReleasePO(po);
                    setIsReleaseModalOpen(true);
                    setIsDrawerOpen(false); // Close details drawer
                }}
            />

            {/* Hold PO Modal */}
            {isHoldModalOpen && holdPO && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white border border-slate-200 rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="bg-red-50 p-4 border-b border-red-200 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-red-600">
                                <AlertCircle size={20} />
                                <h3 className="font-semibold text-lg">Place PO on hold?</h3>
                            </div>
                            <button onClick={() => setIsHoldModalOpen(false)} className="text-slate-400 hover:text-slate-600" disabled={isHolding}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleHoldSubmit} className="p-6 space-y-4">
                            <p className="text-slate-600 text-sm">
                                Explain why PO <span className="font-mono text-slate-900 font-bold">{holdPO.customerPoNumber}</span> is being held. This will block any appointments from checking in or being booked against it.
                            </p>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">Reason for Hold</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:border-red-500"
                                    value={holdReason}
                                    onChange={(e) => setHoldReason(e.target.value)}
                                    disabled={isHolding}
                                >
                                    <option value="damage">Damage/Spoilage</option>
                                    <option value="documentation">Missing Documentation</option>
                                    <option value="quantity_mismatch">Quantity Mismatch</option>
                                    <option value="safety_issue">Safety Issue</option>
                                    <option value="customer_request">Customer Request</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">Additional Details <span className="text-red-500">*</span></label>
                                <textarea
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:border-red-500 min-h-[100px] resize-y"
                                    placeholder="Damaged product, missing paperwork, incorrect quantity, quality issue, carrier issue..."
                                    value={holdNotes}
                                    onChange={(e) => setHoldNotes(e.target.value)}
                                    disabled={isHolding}
                                />
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                                <button type="button" onClick={() => setIsHoldModalOpen(false)} className="px-4 py-2 text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium text-sm" disabled={isHolding}>
                                    Cancel
                                </button>
                                <button type="submit" disabled={isHolding || !holdNotes.trim()} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50 flex items-center gap-2">
                                    {isHolding ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <AlertCircle size={16} />}
                                    Confirm Hold
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Release Hold Modal */}
            {isReleaseModalOpen && releasePO && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white border border-slate-200 rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="bg-emerald-50 p-4 border-b border-emerald-200 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-emerald-600">
                                <CheckCircle2 size={20} />
                                <h3 className="font-semibold text-lg">Release hold?</h3>
                            </div>
                            <button onClick={() => { setIsReleaseModalOpen(false); setReleaseReason(''); }} className="text-slate-400 hover:text-slate-600" disabled={isReleasing}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <p className="text-slate-600 text-sm">
                                Explain why the hold on PO <span className="font-mono text-slate-900 font-bold">{releasePO.customerPoNumber}</span> is being cleared. This will allow carriers to book and check in again.
                            </p>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">Release Reason <span className="text-red-500">*</span></label>
                                <textarea
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:border-emerald-500 min-h-[100px] resize-y"
                                    placeholder="Explain why the hold is being cleared..."
                                    value={releaseReason}
                                    onChange={(e) => setReleaseReason(e.target.value)}
                                    disabled={isReleasing}
                                />
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                                <button type="button" onClick={() => { setIsReleaseModalOpen(false); setReleaseReason(''); }} className="px-4 py-2 text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors font-medium text-sm" disabled={isReleasing}>
                                    Cancel
                                </button>
                                <button onClick={handleReleaseSubmit} disabled={isReleasing || !releaseReason.trim()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50 flex items-center gap-2">
                                    {isReleasing ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <CheckCircle2 size={16} />}
                                    Release Hold
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
                </>
            )}
          </div>

            {/* V1.4A: PO Import Wizard */}
            <POImportWizard
                open={showImportWizard}
                onClose={() => setShowImportWizard(false)}
                onComplete={() => {
                    setShowImportWizard(false);
                    mutate(); // Refresh PO list
                }}
            />
        </div>
    );
}
